import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, from, Observable, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';
import { DashboardStore } from '@/pages/dashboard/dashboard.store';
import { AuthStore } from '@/store/auth.store';
import { RoleStore } from '@/store/role.store';
import { UserStore } from '@/store/user.store';

const AUTH_PATHS = [
  '/auth/login',
  '/auth/register',
  '/auth/refresh',
  '/auth/forgot',
  '/auth/reset',
  '/auth/verify-email',
];

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private authStore = inject(AuthStore);
  private roleStore = inject(RoleStore);
  private userStore = inject(UserStore);
  private dashboardStore = inject(DashboardStore);
  private router = inject(Router);

  private isRefreshingToken = false;
  private refreshSubject = new BehaviorSubject<boolean | null>(null);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    this.authStore.ensureTokensFromStorage();

    const isAuthPath = AUTH_PATHS.some((p) => req.url.includes(p));
    let authReq = req;

    if (!isAuthPath) {
      const headers: Record<string, string> = {};
      const role = this.roleStore.activeRole();
      if (role) headers['X-Effective-Role'] = role;
      const token = this.authStore.tokens()?.accessToken;
      if (token) headers['Authorization'] = `Bearer ${token}`;
      if (Object.keys(headers).length > 0) {
        authReq = req.clone({ setHeaders: headers });
      }
    }

    return next.handle(authReq).pipe(catchError((error) => this.handleError(error, authReq, next)));
  }

  private handleError(
    error: unknown,
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    const status =
      error instanceof HttpErrorResponse ? error.status : (error as { status?: number })?.status;
    const is401 = status === 401;
    const isRefreshPath = req.url.includes('/auth/refresh');

    if (is401 && !isRefreshPath) {
      if (this.isRefreshingToken) {
        return this.refreshSubject.pipe(
          filter((r): r is boolean => r !== null),
          take(1),
          switchMap((success) => (success ? next.handle(req) : throwError(() => error))),
        );
      }

      this.isRefreshingToken = true;
      this.refreshSubject.next(null);

      return from(this.authStore.refresh()).pipe(
        switchMap(() => {
          this.refreshSubject.next(true);
          return next.handle(req);
        }),
        catchError((err) => {
          this.userStore.clear();
          this.dashboardStore.clear();
          this.authStore.logoutLocal();
          this.refreshSubject.next(false);
          this.router.navigate(['/login']);
          return throwError(() => err);
        }),
        finalize(() => {
          this.isRefreshingToken = false;
        }),
      );
    }

    return throwError(() => error);
  }
}
